<template>
  <div v-if="!isNavigating" class="programme-feed">
    <h1 class="text-5xl text-aba-blue p-lg">
      {{programmeObj.title}}
    </h1>
    <div class="px-lg" v-html="description"/>
    <!-- Show audio player for Radio programme, map for others -->
    <div v-if="isRadioProgramme" class="audio-player-container mb-base">
      <audio
        ref="audioPlayer"
        :src="audioUrl"
        controls
        class="w-full"
        preload="metadata">
        Your browser does not support the audio element.
      </audio>
    </div>
    <aba-map v-else-if="!isMentoringProgramme" :markers="markers" :zoom="12"/>
    <post-feed-grid :posts="posts" :processing="processing"/>
  </div>
</template>

<script>
import { db } from '../lib/firebase'
import DOMPurify from 'dompurify'
import PostFeedGrid from './components/PostFeedGrid'
import AbaMap from './components/UI/AbaMap'

export default {
  name: 'ProgrammeFeed',
  components: { AbaMap, PostFeedGrid },
  props: {},

  data: () => ({
    programmeId: null,
    programmeObj: {},
    feed: {},
    unsubscribe: {},
    isNavigating: false
  }),

  computed: {
    posts () {
      return Object.values(this.feed).sort((a, b) => b.date - a.date)
    },
    markers () {
      return this.posts.map(post => {
        const { lat, lng } = post.location || {}
        if (lat && lng) {
          return {
            lat: parseFloat(lat),
            lng: parseFloat(lng),
            label: `#${post.countNumber}`,
            title: post.title,
            description: post.excerpt || '',
            url: post.url || '',
            id: post.id,
            token: post.token || post.id,
            active: false // for marker styling
          }
        }
      }).filter(m => !!m).sort((a, b) => a.lat - b.lat)
    },
    description () {
      if ((this.programmeObj || {}).text) {
        return DOMPurify.sanitize(this.programmeObj.text)
      }
      return ''
    },
    isRadioProgramme () {
      // Check if this is the Radio programme by ID or title
      const radioId = 'L5Ec74lkfbvn5SLyqnSa'
      const title = (this.programmeObj || {}).title || ''
      const isRadio = this.programmeId === radioId ||
             title.toLowerCase().includes('radio')
      console.log('isRadioProgramme check:', {
        programmeId: this.programmeId,
        radioId,
        title,
        isRadio
      })
      return isRadio
    },
    isMentoringProgramme () {
      const mentoringId = 'ZCLF3JDtRXWSgawTsV9w'
      return this.programmeId === mentoringId
    },
    audioUrl () {
      // Get audio URL from programme data
      // Check multiple possible field names
      return (this.programmeObj || {}).audioUrl ||
             (this.programmeObj || {}).audio ||
             (this.programmeObj || {}).streamUrl ||
             (this.programmeObj || {}).stream ||
             ''
    }
  },

  created () {
    this.programmeId = this.$route.params.id
    console.log('ProgrammeFeed created with ID:', this.programmeId) // Debug log
    this.subscribeProgrammeRecord()
    this.subscribeFeed()
  },

  mounted () {
    // Ensure content is visible on mount
    this.isNavigating = false
  },

  watch: {
    $route: {
      immediate: false,
      handler (to, from) {
        // Hide content immediately when navigating away
        if (from && from.name === 'programme' && to && to.name !== 'programme') {
          this.isNavigating = true
        }
        // Reset isNavigating when navigating TO a programme route
        if (to && to.name === 'programme') {
          this.isNavigating = false
        }
        if (!to || to.name !== 'programme' || this.programmeId === to.params.id) {
          // if rooter is changed by popup ... getting in or out of the popup
          return
        }
        this.isNavigating = false
        this.programmeId = to.params.id
        this.unsubscribeAll()
        this.subscribeProgrammeRecord()
        this.subscribeFeed()
      }
    }
  },

  beforeRouteLeave (to, from, next) {
    // Hide content immediately when leaving this route
    this.isNavigating = true
    // Use requestAnimationFrame to ensure DOM update happens before navigation
    requestAnimationFrame(() => {
      next()
    })
  },
  beforeDestroy () {
    this.unsubscribeAll()
  },

  methods: {
    subscribeProgrammeRecord () {
      if (!this.programmeId) return
      console.log('Subscribing to programme record:', this.programmeId) // Debug log
      this.unsubscribe.programme = db.collection('programmes')
        .doc(this.programmeId)
        .onSnapshot(doc => {
          console.log('Programme record received:', doc.exists, doc.data()) // Debug log
          this.programmeObj = { ...doc.data(), id: doc.id }
        }, error => {
          console.error('Error fetching programme record:', error) // Debug log
        })
    },

    subscribeFeed () {
      if (!this.programmeId) return
      this.processing = true
      this.unsubscribe.feed = db.collection('posts')
        .where('partOfProgramme.programmeId', '==', this.programmeId)
        .where('status', '==', 'public')
        .orderBy('date', 'desc')
        .onSnapshot({
          next: querySnapshot => {
            querySnapshot.docChanges().forEach(docChange => {
              const doc = docChange.doc
              switch (docChange.type) {
                case 'added':
                case 'modified':
                  this.$set(this.feed, doc.id, { ...doc.data(), id: doc.id })
                  break
                case 'removed':
                  this.$delete(this.feed, doc.id)
              }
            })
            this.processing = false
          },
          error: err => {
            console.error('getFeed:', err)
            this.processing = false
          }
        })
    },

    unsubscribeAll () {
      Object.values(this.unsubscribe).forEach(un => {
        if (typeof un === 'function') un()
      })
      this.feed = {}
    }
  }
}
</script>

<style lang="scss">
  @import "../styles/vars";
  #app .programme-feed {
    &.navigating-away,
    [style*="display: none"],
    [style*="visibility: hidden"] {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }
    .aba-map {
      height: calc((100vh - #{$base-size}) * 0.4);
    }
    .audio-player-container {
      padding: 1rem 1rem 1rem;
      width: calc(100vw - 2rem);
      background-color: rgba(255, 255, 255, 0.1);
      display: -webkit-box;
      display: -ms-flexbox;
      display: flex;
      -webkit-box-orient: vertical;
      -webkit-box-direction: normal;
      -ms-flex-direction: column;
      flex-direction: column;
      -webkit-box-align: center;
      -ms-flex-align: center;
      align-items: center;
      audio {
        height: 54px;
        outline: none;
        width: 100%;
        opacity: 0.3;
        &::-webkit-media-controls-panel {
          background-color: rgba(255, 255, 255, 0.1);
        }
        &::-webkit-media-controls-play-button,
        &::-webkit-media-controls-volume-slider,
        &::-webkit-media-controls-timeline,
        &::-webkit-media-controls-current-time-display,
        &::-webkit-media-controls-time-remaining-display {
          opacity: 0.5;
        }
      }
    }
  }
</style>
